import type { RetrievedChunkSnapshot } from "../db/types";

export type MatchQuality = "exact" | "fuzzy";

export interface MatchRange {
  start: number;
  end: number;
  quality: MatchQuality;
}

/** Split on ellipsis (ASCII `...` or Unicode `…`), keep segments of ≥3 chars. */
export function splitOnEllipsis(quote: string): string[] {
  return quote
    .split(/\s*(?:\.{3,}|\u2026)\s*/)
    .map((s) => s.trim())
    .filter((s) => s.length >= 3);
}

function normalize(s: string): string {
  return s.toLowerCase().replace(/\s+/g, " ").trim();
}

/** Strip markdown-table cell separators (`|`) from an LLM-quoted segment.
 *  The LLM sometimes serialises a table row as "Cell A | Cell B | Cell C",
 *  but the rendered passage has no pipes (marked turns them into adjacent
 *  <td> elements), so a literal match fails on the `|`. Treat them as
 *  whitespace so the anchor/whitespace-tolerant regex bridges the gaps. */
function stripCellSeparators(s: string): string {
  return s.replace(/\s*\|\s*/g, " ").replace(/\s{2,}/g, " ").trim();
}

/** Typographic variants folded to one form before comparing. */
const CHAR_FOLD: Record<string, string> = {
  "\u2019": "'", "\u2018": "'", "\u201b": "'", "\u00b4": "'", "`": "'",
  "\u201c": '"', "\u201d": '"', "\u201e": '"', "\u00ab": '"', "\u00bb": '"',
  "\u2010": "-", "\u2011": "-", "\u2012": "-", "\u2013": "-", "\u2014": "-", "\u2212": "-",
};
/** Dropped entirely, after folding: markdown markup (absent from the rendered
 *  text, present in the raw chunk), table pipes, dashes (end-of-line
 *  hyphenation, "porte-monnaie" written without its hyphen) and quote marks
 *  (present on one side only). Whitespace is dropped too. */
const CHAR_DROP = new Set(["*", "_", "#", "\\", "|", "-", "'", '"', "\u00ad"]);

/** A comparison form of `s` with, for each of its characters, the index of the
 *  original character it came from. Lower-cased, accents and ligatures
 *  decomposed (NFKD), quotes, dashes and markup dropped, and ALL whitespace
 *  removed — so double spaces, line breaks, non-breaking spaces, French
 *  spacing before « ; : ? ! », hyphenated line ends and curly vs straight
 *  quotes no longer matter. */
function canonical(s: string): { text: string; map: number[] } {
  let text = "";
  const map: number[] = [];
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    const folded = CHAR_FOLD[ch] ?? ch;
    if (/\s/.test(folded) || CHAR_DROP.has(folded)) continue;
    const decomposed = folded.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    for (const c of decomposed) {
      if (/\s/.test(c)) continue;
      text += c;
      map.push(i);
    }
  }
  return { text, map };
}

/** Typography-tolerant exact match (see `canonical`), mapped back to `concat`. */
function findCanonical(concat: string, segment: string): MatchRange | null {
  const seg = canonical(segment).text;
  if (seg.length < 3) return null;
  const hay = canonical(concat);
  const idx = hay.text.indexOf(seg);
  if (idx < 0) return null;
  return { start: hay.map[idx], end: hay.map[idx + seg.length - 1] + 1, quality: "exact" };
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Sequential anchor match: walk the anchor words in order, allowing up to
 *  ~30 chars between consecutive matches. Words not found within that gap are
 *  skipped — handles LLM-inserted connectors ("are", "and") that aren't in the
 *  source (e.g. when the LLM synthesises a sentence from several table cells).
 *  Returns the span from first to last matched word, provided ≥60% of anchor
 *  words were found in order. */
function matchAnchorWords(
  lower: string,
  words: string[],
  fromIdx: number,
  maxGap: number,
): { start: number; end: number } | null {
  let pos = fromIdx;
  let firstStart = -1;
  let lastEnd = -1;
  let matched = 0;

  for (const word of words) {
    const w = word.toLowerCase();
    const found = lower.indexOf(w, pos);
    if (found < 0) continue;
    if (firstStart >= 0 && found - lastEnd > maxGap) continue;

    if (firstStart < 0) firstStart = found;
    lastEnd = found + word.length;
    matched++;
    pos = lastEnd;
  }

  const threshold = Math.ceil(words.length * 0.6);
  if (firstStart < 0 || matched < threshold) return null;
  return { start: firstStart, end: lastEnd };
}

/** Anchor-based fuzzy match: locate the first N and last N words of `segment`
 *  inside the concat, then return the span between them.
 *  Tolerates: missing whitespace between tokens, LLM-inserted connector words
 *  not present in the source, and synthesised quotes that stitch together
 *  several table cells / phrases. The combined span is still capped at
 *  ~2.2× the quote length to reject unrelated long-distance matches. */
function findAnchored(concat: string, segment: string): MatchRange | null {
  const words = segment.trim().split(/\s+/);
  if (words.length < 6) return null;

  const nAnchor = Math.min(5, Math.max(3, Math.floor(words.length / 3)));
  const startWords = words.slice(0, nAnchor);
  const endWords = words.slice(-nAnchor);

  const lower = concat.toLowerCase();
  const startSpan = matchAnchorWords(lower, startWords, 0, 30);
  if (!startSpan) return null;

  const endSpan = matchAnchorWords(lower, endWords, startSpan.end, 30);
  if (!endSpan) return null;

  if (endSpan.end - startSpan.start > segment.length * 2.2) return null;

  return { start: startSpan.start, end: endSpan.end, quality: "fuzzy" };
}

/** Last-resort: highlight the first ~6 words and last ~6 words of the segment
 *  as INDEPENDENT ranges. Returns 0, 1, or 2 ranges. */
function findEndpoints(concat: string, segment: string): MatchRange[] {
  const words = segment.trim().split(/\s+/);
  if (words.length < 4) return [];

  const n = Math.min(6, Math.max(3, Math.floor(words.length / 2)));
  const first = words.slice(0, n).join(" ");
  const last = words.slice(-n).join(" ");

  const firstPattern = escapeRegex(first).replace(/\s+/g, "\\s*");
  const lastPattern = escapeRegex(last).replace(/\s+/g, "\\s*");

  const lower = concat.toLowerCase();
  const ranges: MatchRange[] = [];

  const firstMatch = new RegExp(firstPattern, "i").exec(lower);
  if (firstMatch) {
    ranges.push({
      start: firstMatch.index,
      end: firstMatch.index + firstMatch[0].length,
      quality: "fuzzy",
    });
  }

  const lastRe = new RegExp(lastPattern, "ig");
  lastRe.lastIndex = firstMatch ? firstMatch.index + firstMatch[0].length : 0;
  const lastMatch = lastRe.exec(lower);
  if (lastMatch) {
    ranges.push({
      start: lastMatch.index,
      end: lastMatch.index + lastMatch[0].length,
      quality: "fuzzy",
    });
  }

  return ranges;
}

function mapNormalizedIndex(original: string, normalizedIdx: number): number {
  let origIdx = 0;
  let normIdx = 0;
  let prevWhitespace = true;
  while (origIdx < original.length && normIdx < normalizedIdx) {
    const c = original[origIdx];
    if (/\s/.test(c)) {
      if (!prevWhitespace) normIdx++;
      prevWhitespace = true;
    } else {
      normIdx++;
      prevWhitespace = false;
    }
    origIdx++;
  }
  while (origIdx < original.length && /\s/.test(original[origIdx])) origIdx++;
  return origIdx;
}

function extendToOriginal(
  original: string,
  startIdx: number,
  normalizedTarget: string,
): string {
  let end = startIdx;
  let targetIdx = 0;
  while (end < original.length && targetIdx < normalizedTarget.length) {
    const origChar = original[end];
    const targetChar = normalizedTarget[targetIdx];
    if (/\s/.test(origChar)) {
      end++;
      while (end < original.length && /\s/.test(original[end])) end++;
      if (targetChar === " ") targetIdx++;
    } else {
      if (origChar !== targetChar) return "";
      end++;
      targetIdx++;
    }
  }
  return original.slice(startIdx, end);
}

/** Locate `segment` inside `concat`. Tries in order:
 *  1. exact
 *  2. case-insensitive
 *  2b. typography-tolerant (whitespace, quotes, dashes, hyphenation, markup)
 *  3. whitespace-and-case normalized
 *  4. anchor-based fuzzy (contiguous span bounded by start/end anchors)
 *  5. endpoints-only fuzzy (independent highlight of first and last N words)
 *  Returns 0, 1, or 2 ranges. */
export function findSegment(concat: string, rawSegment: string): MatchRange[] {
  // Steps 1-2 use the raw segment (best fidelity if the quote was reproduced
  // verbatim). Steps 3-5 use the pipe-stripped form so that LLM-serialised
  // table rows (`Cell A | Cell B | …`) match the pipe-free DOM concat.
  const exact = concat.indexOf(rawSegment);
  if (exact >= 0)
    return [{ start: exact, end: exact + rawSegment.length, quality: "exact" }];

  const ci = concat.toLowerCase().indexOf(rawSegment.toLowerCase());
  if (ci >= 0)
    return [{ start: ci, end: ci + rawSegment.length, quality: "exact" }];

  const segment = stripCellSeparators(rawSegment);

  // Same words, different typography (spaces, quotes, dashes, hyphenation…).
  const canonicalMatch = findCanonical(concat, rawSegment);
  if (canonicalMatch) return [canonicalMatch];

  const nConcat = normalize(concat);
  const nSeg = normalize(segment);
  const nIdx = nConcat.indexOf(nSeg);
  if (nIdx >= 0) {
    const origStart = mapNormalizedIndex(concat.toLowerCase(), nIdx);
    const matched = extendToOriginal(concat.toLowerCase(), origStart, nSeg);
    if (matched)
      return [
        { start: origStart, end: origStart + matched.length, quality: "exact" },
      ];
  }

  const anchored = findAnchored(concat, segment);
  if (anchored) return [anchored];

  return findEndpoints(concat, segment);
}

export interface AlternativeMatch {
  ref: RetrievedChunkSnapshot;
  segmentsMatched: number;
  segmentsTotal: number;
  exactSegments: number;
  fuzzySegments: number;
}

/** Search non-cited chunks for the LLM's quote. Used when the cited chunk doesn't
 *  contain the quote — likely a chunk-number confusion by the LLM. Each candidate
 *  is scored by how many segments matched (exact-first), tied by retrieval score.
 *  Returns up to `maxResults` candidates, never the cited chunks. */
export function findQuoteAlternatives(
  quote: string,
  citedNumbers: Set<number>,
  corpus: RetrievedChunkSnapshot[],
  maxResults = 3,
): AlternativeMatch[] {
  const segments = splitOnEllipsis(quote);
  if (segments.length === 0) return [];

  const candidates: AlternativeMatch[] = [];
  for (const ref of corpus) {
    if (citedNumbers.has(ref.number)) continue;

    let exactSegments = 0;
    let fuzzySegments = 0;
    for (const seg of segments) {
      const ranges = findSegment(ref.text, seg);
      if (ranges.length === 0) continue;
      const allExact = ranges.every((r) => r.quality === "exact");
      if (allExact) exactSegments++;
      else fuzzySegments++;
    }
    const segmentsMatched = exactSegments + fuzzySegments;
    if (segmentsMatched === 0) continue;

    candidates.push({
      ref,
      segmentsMatched,
      segmentsTotal: segments.length,
      exactSegments,
      fuzzySegments,
    });
  }

  candidates.sort((a, b) => {
    if (b.segmentsMatched !== a.segmentsMatched)
      return b.segmentsMatched - a.segmentsMatched;
    if (b.exactSegments !== a.exactSegments)
      return b.exactSegments - a.exactSegments;
    return b.ref.score - a.ref.score;
  });

  return candidates.slice(0, maxResults);
}
